import { Typography } from '@mui/joy';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { AgGridTable } from 'core/components';
import { SortDirection, TableLayoutType } from 'core/enums';
import { AgGridClientTableProvider, SelectionCountProvider } from 'core/states';
import { ISubmitOrderLogEntry } from 'dashboard/types';
import { Level } from 'theme/enums';

import { columnDef } from './columns';
import { SubmitOrderLogColId } from './enums';

interface IProps {
  entries: ISubmitOrderLogEntry[];
  isLoading: boolean;
}

export const SubmitOrderLogTable = ({ entries, isLoading }: IProps) => {
  const { t } = useTranslation();

  const columnDefs = useMemo(() => columnDef(), []);

  // Memoized: AgGridTableView keys a delayed-visibility timer on this element's
  // identity, so a fresh element every render defers the empty state forever.
  const emptyStateElement = useMemo(
    () => <Typography level={Level.BODY_MD}>{t('messageNoSubmitOrderLogEntries')}</Typography>,
    [t]
  );

  // Highest order number first. The BE sends entries newest-first by submission
  // time, which scatters the repeated attempts on one order down the list; sorting
  // on the order itself keeps those attempts together and still puts the most
  // recent orders on top. A user's own sort (saved filter set or a header click)
  // takes precedence — useAgGridFilter only falls back to this.
  const initialState = useMemo(
    () => ({
      sort: {
        sortModel: [{ colId: SubmitOrderLogColId.ORDER_ID, sort: SortDirection.DESC }],
      },
    }),
    []
  );

  return (
    <SelectionCountProvider>
      <AgGridClientTableProvider
        tableId="submitOrderLogTable"
        entityType="Submission"
        layoutType={TableLayoutType.SUBMIT_ORDER_LOG}
        columnDefs={columnDefs}
        initialState={initialState}
        data={entries}
        isDataLoading={isLoading}
        loadingPercentage={isLoading ? 0 : 100}
        dataCurrentPage={1}
        hasRowSelection={false}
        hasLayoutActions
        hasManageFilters
      >
        <AgGridTable emptyStateElement={emptyStateElement} />
      </AgGridClientTableProvider>
    </SelectionCountProvider>
  );
};
